import capitalize from 'lodash/capitalize';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import { datadogRum } from 'root/datadog/datadogRum';

import { CustomerInfoSubmissionStage } from './index';
import { CfdEmail, CfdPhone } from './types';

export interface RegisterCFDCustomer {
  saleId?: string;
  customerId?: string;
  firstName: string;
  lastName: string;
  phones: Partial<{ [phone in CfdPhone]: string }>;
  emails: Partial<{ [email in CfdEmail]: string }>;
}

type RegisterCFDCustomerParams = RequireAtLeastOne<
  RegisterCFDCustomer,
  'firstName' | 'lastName' | 'phones' | 'emails'
>;

export const REGISTER_CUSTOMER_SAVED = 'REGISTER_CUSTOMER_SAVED';
const SORT_BY = '&sort=firstName';

export const errorMessages = {
  invalidSaleId: 'Unable to attach customer to current sale. Try again.',
  missingInformation: 'Unable to create or update customer. Try again.',
  apiError: 'Unable to add customer to sale. Try again or contact support.',
  canceled: 'Customer canceled on CFD',
};

export const setRegisterCustomer = async ({
  saleId,
  customerId,
  firstName,
  lastName,
  phones = {} as RegisterCFDCustomer['phones'],
  emails = {} as RegisterCFDCustomer['emails'],
}: RegisterCFDCustomerParams) => {
  const isCustomerAttached = customerId !== undefined;
  const isInRegister = window.location.pathname === '/register.php';

  // Start closure
  const customerUpdateCreatorFunction = () => {
    return async (customerSelectedByCashier?: string, skipUpdate: boolean = false) => {
      try {
        // If pathname is register then customer info needs to be attached to a sale
        const systemCustomerId = window.merchantos.session.getSystemCustomerID();
        const hasPhones = Object.keys(phones).length;
        const hasEmails = Object.keys(emails).length;
        const currentSaleId = parseInt(
          document.getElementById('register')?.getAttribute('transaction_id') || '',
          10
        );
        const method = customerSelectedByCashier ? 'PUT' : 'POST';

        if (isInRegister && saleId && parseInt(saleId, 10) !== currentSaleId) {
          cancelRegisterCustomer(errorMessages.invalidSaleId);
          return;
        }

        if (!firstName && !lastName && !hasPhones && !hasEmails) {
          cancelRegisterCustomer(errorMessages.missingInformation);
        }

        let customerToBeAttached;
        if (!skipUpdate) {
          const res = await window.fetch(
            `/API/V3/Account/${systemCustomerId}/Customer${
              customerSelectedByCashier ? `/${customerSelectedByCashier}` : ''
            }.json`,
            {
              method,
              body: JSON.stringify(buildPostBody({ firstName, lastName, emails, phones })),
            }
          );

          if (res.status !== 200) {
            throw new Error('Bad request');
          }

          const {
            Customer: { customerID: createdCustomerId },
          } = await res.json();
          customerToBeAttached = createdCustomerId;
        }

        customerToBeAttached = customerToBeAttached || customerSelectedByCashier;

        if (customerToBeAttached && saleId) {
          const resSale = await attachCustomerToSale({
            customerID: customerToBeAttached,
            saleId,
            systemCustomerId: systemCustomerId as string,
          });

          if (resSale.status !== 200) {
            throw new Error('Bad request');
          }
        }

        if (isInRegister) {
          // @NOTE Not removing this query string creates a new transaction
          window.location.href = window.location.href
            .replace('new_transaction', '')
            .replace('clear_customer', '');
        }

        window.sessionStorage.setItem(
          REGISTER_CUSTOMER_SAVED,
          'SAVED' as CustomerInfoSubmissionStage
        );
      } catch (err) {
        cancelRegisterCustomer(errorMessages.apiError, 'alertUser');
        datadogRum.addError(err);
      }
    };
  };

  const createOrUpdateCustomerInfo = customerUpdateCreatorFunction();

  if (!isCustomerAttached) {
    // If there is no attached customer, check if the customer exists in the system
    let existingCustomerList = [] as Customer[];
    try {
      existingCustomerList = await getMatchingCustomersInSystem(emails.primary, phones.mobile);
    } catch (err) {
      cancelRegisterCustomer(errorMessages.apiError, 'alertUser');
      datadogRum.addError(err);
      return;
    }

    if (existingCustomerList.length > 0) {
      // Ask cashier to take action on users
      window.sessionStorage.setItem(
        REGISTER_CUSTOMER_SAVED,
        'HAS_DUPLICATES' as CustomerInfoSubmissionStage
      );

      window.cfdSetExistingCustomers(existingCustomerList, createOrUpdateCustomerInfo, {
        firstName,
        lastName,
        phones,
        emails,
      });
    } else {
      // Create a new customer when the customer is not in database
      await createOrUpdateCustomerInfo();
    }
  } else {
    await createOrUpdateCustomerInfo(customerId);
  }
};

const buildPostBody = ({
  firstName,
  lastName,
  phones = {},
  emails = {},
}: RegisterCFDCustomerParams) => {
  const hasPhones = Object.keys(phones).length;
  const hasEmails = Object.keys(emails).length;

  return {
    ...(firstName ? { firstName } : null),
    ...(lastName ? { lastName } : null),
    ...(hasPhones || hasEmails
      ? {
          Contact: {
            ...(hasPhones
              ? {
                  Phones: {
                    ContactPhone: Object.entries(phones).map(([type, number]) => ({
                      useType: capitalize(type),
                      number,
                    })),
                  },
                }
              : null),
            ...(hasEmails
              ? {
                  Emails: {
                    ContactEmail: Object.entries(emails).map(([type, address]) => ({
                      useType: capitalize(type),
                      address,
                    })),
                  },
                }
              : null),
          },
        }
      : null),
  };
};

const attachCustomerToSale = ({
  customerID,
  systemCustomerId,
  saleId,
}: {
  customerID: string;
  systemCustomerId: string;
  saleId: string;
}) => {
  return window.fetch(`/API/V3/Account/${systemCustomerId}/Sale/${saleId}.json`, {
    method: 'PUT',
    body: JSON.stringify({
      customerID,
    }),
  });
};

const getMatchingCustomersInSystem = async (
  primaryEmail: string | undefined,
  mobilePhone: string | undefined
) => {
  const finalQueryArray = queryBuilder(primaryEmail, mobilePhone);
  let hasError = false;
  const response =
    finalQueryArray?.length > 0
      ? await Promise.allSettled([
          ...finalQueryArray.map((query) =>
            window
              .fetch(query)
              .then((res) => {
                if (res.status !== 200) hasError = true;
                return res.json();
              })
              .then((items) => items?.Customer)
          ),
        ])
      : [];

  if (hasError) throw new Error('Bad request');

  const customerList = response
    ?.map(({ value }: any) => (Array.isArray(value) ? value : [value]))
    .flat()
    .filter((item) => item !== undefined);

  return dedupeSortByMatchAndName(customerList, primaryEmail, mobilePhone);
};

export const queryBuilder = (email: string | undefined, phone: string | undefined) => {
  const hasNewCustomerSearch = window.lsCloud.Modules.hasModule('customer.search.optimized_search');
  const accountId = window.merchantos.session.getSystemCustomerID() as string;
  const newSearchQuery = (search: string) =>
    `/API/V3/Account/${accountId}/Customer/Search.json?query=${search}&load_relations=[%22Contact%22]`;
  const legacySearchBaseQuery = `/API/V3/Account/${accountId}/Customer.json?load_relations=[%22Contact%22]&or=`;

  const phoneSearchRelationsQuery = ['mobile', 'phoneHome', 'phoneWork', 'pager', 'fax'] // All the phone fields in the backend db
    .map((field) => `Contact.${field}%3D~,${phone}`)
    .join('%7C');

  const emailSearchRelationsQuery = ['email', 'email2'] // All the email fields in the backend db
    .map((field) => `Contact.${field}%3D~,${email}`)
    .join('%7C');

  const finalQuery = [];

  if (email && phone) {
    if (hasNewCustomerSearch) {
      finalQuery.push(`${newSearchQuery(email)}${SORT_BY}`);
      finalQuery.push(`${newSearchQuery(phone)}${SORT_BY}`);
    } else {
      finalQuery.push(
        `${legacySearchBaseQuery}${emailSearchRelationsQuery}|${phoneSearchRelationsQuery}${SORT_BY}`
      );
    }
  }

  if (email && !phone) {
    if (hasNewCustomerSearch) {
      finalQuery.push(`${newSearchQuery(email)}${SORT_BY}`);
    } else {
      finalQuery.push(`${legacySearchBaseQuery}${emailSearchRelationsQuery}${SORT_BY}`);
    }
  }

  if (phone && !email) {
    if (hasNewCustomerSearch) {
      finalQuery.push(`${newSearchQuery(phone)}${SORT_BY}`);
    } else {
      finalQuery.push(`${legacySearchBaseQuery}${phoneSearchRelationsQuery}${SORT_BY}`);
    }
  }
  return finalQuery;
};

const matchesUniqueField = (field: any, matchValue?: string, matchKey?: 'Phone' | 'Email') => {
  const subKey = matchKey === 'Phone' ? 'number' : 'address';
  if (typeof field === 'string' || !matchValue || !matchKey) return false;
  return Array.isArray(field?.[`Contact${matchKey}`])
    ? field?.[`Contact${matchKey}`]?.some((mail: any) => mail?.[subKey] === matchValue)
    : field?.[`Contact${matchKey}`]?.[subKey] === matchValue;
};

export const dedupeSortByMatchAndName = (
  list: Customer[],
  primaryEmail?: string,
  mobilePhone?: string
) => {
  const hasCustomerInfoConfirmation = window.lsCloud.Modules.hasModule(
    'register.cfd.info_confirmation'
  );

  const uniqueCustomers = uniqBy(list, 'customerID');
  return sortBy(uniqueCustomers, [
    // Put the customers that have matching email and phone first
    ...(hasCustomerInfoConfirmation
      ? [
          (customer: any) =>
            matchesUniqueField(customer?.Contact?.Phones, mobilePhone, 'Phone') &&
            matchesUniqueField(customer?.Contact?.Emails, primaryEmail, 'Email')
              ? -1
              : 1,
        ]
      : []),
    (customer) => customer?.firstName?.toLowerCase(),
    (customer) => customer?.lastName?.toLowerCase(),
  ]);
};

export const cancelRegisterCustomer = (
  message?: string,
  type: 'warnUser' | 'alertUser' = 'warnUser'
) => {
  window[type]?.(message || errorMessages.canceled);
  // @ts-ignore
  window.cancelRegisterCustomer?.();
};

export const buildAddress = (contactAddress: ContactAddress) => {
  if (Object.values(contactAddress).every((entry) => entry.trim().length === 0)) return undefined;

  return Object.values(contactAddress).reduce((coll, next) => {
    const trimmedEntry = next.trim();
    if (coll.length) {
      return trimmedEntry.length ? `${coll}, ${trimmedEntry}` : coll;
    }
    return trimmedEntry;
  }, '') as string;
};
